import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NoteService } from '@app/_components/task/note/note.service';
import { AuthenticationService } from '@app/_services/authentication.service';
import { AlertService } from '@app/_services/alert.service';
import { Subscription, take } from 'rxjs';

export interface NoteItem {
  noteId?: number;
  dateTime: Date;
  employee: string;
  notes: string;
  noteType: number;
  status: number;
  isExpanded?: boolean;
  title?: string;
}

export interface NotesChatConfig {
  title: string;
  maxWidth?: string;
  maxHeight?: string;
  // New sizing options
  width?: string;      // e.g., '100%', '100vw', '600px'
  height?: string;     // e.g., '100%', '100vh', '500px'
  fillParent?: boolean; // if true, component stretches to fill parent container
  customClass?: string;
  fieldsPosition?: 'top' | 'bottom';
  showFilters?: boolean;
  showSortOptions?: boolean;
  sortByDate?: 'asc' | 'desc' | 'none';
  allowEdit?: boolean;
  allowDelete?: boolean;
  allowAdd?: boolean;
  showUserFilters?: boolean;
  emptyNotesText?: string;
  placeholder?: string;
  theme?: 'default' | 'compact' | 'minimal';
  maxNoteLength?: number;
  editButtonText?: string;
  deleteButtonText?: string;
  cancelButtonText?: string;
  showHeader?: boolean;
  highlightTypes?: Array<{name: string, color: string }>;
  noteTypes?: Array<{ value: number; name: string; color: string }>;
  statusOptions?: Array<{ value: number; name: string; color: string }>;
  availableFilters?: Array<{ key: string; label: string; type: 'text' | 'select' | 'date' }>;
  noOfRows?: number;
  useRichTextEditor?: boolean;
}

@Component({
    selector: 'app-notes-chat',
    templateUrl: './notes-chat.component.html',
    styleUrls: ['./notes-chat.component.css'],
    standalone: false
})
export class NotesChatComponent implements OnInit, OnDestroy, OnChanges {

  @Input() config: NotesChatConfig | null = null;
  readonly instanceId: string = 'notes-' + Math.random().toString(36).substr(2, 9);
  notes: NoteItem[] = [];
  @Input() noteSourceId?: number;
  @Input() noteSource?: number;
  
  managingNote: boolean = false;
  isSubmitting: boolean = false;
  
  @Output() noteAdded = new EventEmitter<Partial<NoteItem>>();
  @Output() noteUpdated = new EventEmitter<{ note: NoteItem; index: number }>();
  @Output() noteDeleted = new EventEmitter<{ note: NoteItem; index: number }>();
  @Output() filtersChanged = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<'asc' | 'desc' | 'none'>();
  
  notesForm: UntypedFormGroup;
  filterForm: UntypedFormGroup;
  showFilterDropdown: boolean = false;
  isEditingNote: boolean = false;
  editingNoteIndex: number = -1;
  currentSortOrder: 'asc' | 'desc' | 'none' = 'asc';
  showFullscreen: boolean = false;
  showFullscreenEditor: boolean = false;
  
  finalConfig: NotesChatConfig;
  activeFilters: any = {};
  
  // User multiselect
  uniqueUsers: string[] = [];
  selectedUsers: string[] = [];
  userOptions: Array<{ label: string; value: string }> = [];
  editorInstanceId: string = 'notes-editor-' + Math.random().toString(36).substr(2, 9);
  popupEditorCycle = 0;
  
  tinyMceConfig: any;
  
  private subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private noteService: NoteService,
    private authenticationService: AuthenticationService,
    private alertService: AlertService,
    private sanitizer: DomSanitizer
  ) {
    this.setTinyMceInit();
    this.notesForm = this.formBuilder.group({
      newNoteTitle: [''],
      newNoteText: [''],
      newNoteType: [1],
      newNoteStatus: [0]
    });
    
    this.filterForm = this.formBuilder.group({
      employee: [''],
      noteType: [''],
      status: [''],
      createdDate: [''],
      noteText: ['']
    });
  }

  ngOnInit(): void {
    this.initializeConfiguration();
    this.setupFilterForm();
    this.loadNotes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      console.log('Config changed:', changes['config']);
      this.initializeConfiguration();
    }
    
    // Reload notes when noteSourceId or noteSource changes
    if ((changes['noteSourceId'] && !changes['noteSourceId']?.firstChange ) || (changes['noteSource'] && !changes['noteSource']?.firstChange)) {
      this.loadNotes();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeConfiguration(): void {
    // Default configuration
    const defaultConfig: NotesChatConfig = {
      title: 'Notes',
      maxWidth: '100%',
      maxHeight: '500px',
      width: '100%',
      height: undefined,
      fillParent: false,
      customClass: '',
      fieldsPosition: 'top',
      showFilters: true,
      showSortOptions: true,
      sortByDate: 'asc',
      allowEdit: true,
      allowDelete: true,
      allowAdd: true,
      emptyNotesText: 'No notes added',
      placeholder: 'Enter remarks...',
      theme: 'default',
      maxNoteLength: 150,
      noOfRows: 2,
      editButtonText: 'Edit Note',
      deleteButtonText: 'Delete Note',
      cancelButtonText: 'Cancel',
      showHeader: true,
      showUserFilters: true,
      noteTypes: [
        { value: 1, name: 'General', color: '#DCDCDC' },
        { value: 2, name: 'Action Item', color: '#ffcccc' },
        { value: 4, name: 'Meeting Notes', color: '#ade061' },
        { value: 5, name: 'Status Update', color: '#FFFF99' }
      ],
      statusOptions: [
        { value: 0, name: 'Open', color: '#D3D3D3' },
        { value: 1, name: 'Complete', color: '#90EE90' },
        { value: 2, name: 'Proposed', color: '#FFE599' },
        { value: 3, name: 'On Hold', color: '#ADD8E6' },
        { value: 4, name: 'Cancelled', color: '#F4A6A6' },
        { value: 5, name: 'In Progress', color: '#FFA500' }
      ],
      availableFilters: [
        { key: 'employee', label: 'Employee Name', type: 'text' },
        { key: 'noteType', label: 'Note Type', type: 'select' },
        { key: 'status', label: 'Status', type: 'select' },
        { key: 'createdDate', label: 'Created Date', type: 'date' },
        { key: 'noteText', label: 'Note Text', type: 'text' }
      ],
      highlightTypes: [],
      useRichTextEditor: false
    };

    // Merge user config with defaults
    this.finalConfig = { ...defaultConfig, ...(this.config || {}) };
    this.currentSortOrder = this.finalConfig.sortByDate || 'asc';
    
    // Set note form defaults
    this.notesForm.patchValue({
      newNoteType: this.finalConfig.noteTypes?.[0]?.value || 1,
      newNoteStatus: this.finalConfig.statusOptions?.[0]?.value || 0
    });
    
    // Apply sorting
    this.sortNotes();
  }

  // Compute root container inline styles for width/height behavior
  getRootStyle(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    if (this.finalConfig.fillParent) {
      styles['width'] = '100%';
      styles['height'] = '100%';
      styles['display'] = 'flex';
      styles['flex-direction'] = 'column';
    } else {
      const width = this.finalConfig.width || this.finalConfig.maxWidth || '100%';
      styles['width'] = width;
      if (this.finalConfig.height) {
        styles['height'] = this.finalConfig.height;
        styles['display'] = 'flex';
        styles['flex-direction'] = 'column';
      }
    }
    return styles;
  }

  // Compute chat messages container style to either use max-height or flex-fill
  getMessagesContainerStyle(fullWidth: boolean = false): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    if (this.finalConfig.fillParent || this.finalConfig.height) {
      styles['flex'] = '1 1 auto';
      !fullWidth ? styles['overflow-y'] = 'auto' : '';
      styles['max-height'] = 'unset';
      styles['min-height'] = '0';
    } else {
      styles['max-height'] = this.finalConfig.maxHeight ? this.finalConfig.maxHeight : '500px';
      styles['overflow-y'] = 'auto';
    }
    return styles;
  }

  // Ensure the chat interface container flex-fills within the root when filling parent/explicit height
  getChatInterfaceContainerStyle(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    if (this.finalConfig.fillParent || this.finalConfig.height) {
      styles['flex'] = '1 1 auto';
      styles['min-height'] = '0';
      styles['display'] = 'flex';
      styles['flex-direction'] = 'column';
    }
    return styles;
  }

  setTinyMceInit(): void {
    this.tinyMceConfig = {
      base_url: '/tinymce',
      suffix: '.min',
      plugins: 'code anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount autoresize textcolor',
      toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
      menubar: false,
      statusbar: true,
      min_height: 250,
      max_height: 500,
      autoresize_bottom_margin: 16,
      placeholder: this.finalConfig?.placeholder || 'Enter Description'
    };
  }

  private setupFilterForm(): void {
    if (this.finalConfig.availableFilters) {
      const filterControls: { [key: string]: UntypedFormControl } = {};
      this.finalConfig.availableFilters.forEach(filter => {
        filterControls[filter.key] = new UntypedFormControl('');
      });
      this.filterForm = this.formBuilder.group(filterControls);
    }
  }

  private loadNotes(): void {
    try {
      if (!this.noteSourceId || !this.noteSource) return;
      
      const event: any = { first: 0, rows: 150, sortField: 'createDate', sortOrder: 0, multiSortMeta: [], globalFilter: null, filters: {} };
      const pageNumber = 0;
      this.managingNote = true;
      
      this.noteService.findBySource(
        pageNumber, event.rows, event.sortField, event.sortOrder, event.multiSortMeta,
        event.globalFilter, event.filters, this.noteSource, this.noteSourceId
      ).pipe(take(1)).subscribe(
        res => {
          this.notes = (res?.data?.notes || []).map((n: any) => ({
            noteId: n.noteId,
            dateTime: n.createDate ? new Date(n.createDate) : new Date(),
            employee: (n?.createdBy?.firstName || "") + ' ' +  (n?.createdBy?.lastName || ''),
            noteType: n.noteType,
            status: n.status,
            notes: n.description,
            title: n.title || '',
            isExpanded: false
          }));
          this.updateUniqueUsers();
          this.sortNotes();
        },
        err => { this.alertService.error(err); this.managingNote = false; },
        () => { this.managingNote = false; }
      );
    } catch (e) { this.managingNote = false; }
  }

  getFilteredNotes(): NoteItem[] {
    if (!this.notes) return [];
    
    return this.notes.filter(note => {
      // Filter by selected users (multiselect)
      const employeeName = note.employee.trim();
      if (this.selectedUsers.length > 0 && !this.selectedUsers.includes(employeeName)) {
        return false;
      }
      
      // Apply other filters
      const matches = Object.keys(this.activeFilters).every(key => {
        const filterValue = this.activeFilters[key];
        if (!filterValue) return true;
        
        switch (key) {
          case 'employee':
            return note.employee.toLowerCase().includes(filterValue.toLowerCase());
          case 'noteType':
          case 'status':
            return note[key].toString() === filterValue;
          case 'noteText':
            return note.notes.toLowerCase().includes(filterValue.toLowerCase());
          case 'createdDate':
            return note.dateTime && note.dateTime.toISOString().split('T')[0] === filterValue;
          default:
            return true;
        }
      });
      
      return matches;
    }).sort((a, b) => {
      if (this.currentSortOrder === 'none') return 0;
      
      const aTime = a.dateTime ? new Date(a.dateTime).getTime() : 0;
      const bTime = b.dateTime ? new Date(b.dateTime).getTime() : 0;
      
      return this.currentSortOrder === 'asc' ? (aTime - bTime) : (bTime - aTime);
    });
  }

  addNewNote(): void {
    if (this.managingNote || this.isSubmitting || !this.canSubmitNote()) {
      return;
    }

    if (this.isEditingNote && this.editingNoteIndex >= 0) {
      this.updateExistingNote();
    } else {
      this.createNewNote();
    }
  }

  private isNoteTextEmpty(text: string | null | undefined): boolean {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      return true;
    }
    return trimmed === '<p></p>' || trimmed === '<p><br></p>';
  }

  canSubmitNote(): boolean {
    const noteTitle = this.notesForm.get('newNoteTitle')?.value;
    const noteText = this.notesForm.get('newNoteText')?.value;

    const hasTitle = !!(noteTitle && noteTitle.toString().trim());
    const hasText = !this.isNoteTextEmpty(noteText);

    return hasTitle || hasText;
  }

  private createNewNote(): void {
    const noteText = this.notesForm.get('newNoteText')?.value;
    const noteTitle = this.notesForm.get('newNoteTitle')?.value || '';
    const currentUser = this.authenticationService.currentUserValue;
    
    const payload: any = {
      noteSource: this.noteSource || 2,
      noteSourceId: this.noteSourceId,
      title: noteTitle.trim(),
      description: noteText.trim(),
      status: this.notesForm.get('newNoteStatus')?.value,
      noteType: this.notesForm.get('newNoteType')?.value,
      visibility: 1,
      createdBy: currentUser ? { userId: currentUser.userId } : undefined
    };

    this.isSubmitting = true;
    this.managingNote = true;
    const sub = this.noteService.add(payload).pipe(take(1)).subscribe(
      res => {
        const saved = res?.data?.note || payload;
        const newNote: NoteItem = {
          noteId: saved.noteId,
          dateTime: saved.createDate ? new Date(saved.createDate) : saved.lastUpdate ? new Date(saved.lastUpdate) : new Date(),
          employee: (currentUser?.firstName || '') + ' ' + (currentUser?.lastName || ''),
          noteType: saved.noteType,
          status: saved.status,
          notes: saved.description,
          title: saved.title || ''
        };

        this.noteAdded.emit(newNote);
        this.resetForm();
        this.showFullscreenEditor = false;
        this.loadNotes(); // Reload notes to get latest from server
      },
      err => {
        // Do NOT reset isSubmitting on error. The server may have already saved the note
        // (504 gateway timeout means the backend completed but Apache timed out). Keeping
        // isSubmitting=true prevents a second submit until the user explicitly reloads.
        this.alertService.error(err);
        this.managingNote = false;
      },
      () => { this.isSubmitting = false; /* note: managingNote is owned by loadNotes() from here */ }
    );
    
    this.subscriptions.push(sub);
  }

  private updateExistingNote(): void {
    const existing = this.notes[this.editingNoteIndex];
    const noteText = this.notesForm.get('newNoteText')?.value;
    const noteTitle = this.notesForm.get('newNoteTitle')?.value || '';
    
    const payload: any = {
      noteId: existing.noteId,
      noteSource: this.noteSource || 2,
      noteSourceId: this.noteSourceId,
      title: noteTitle.trim(),
      description: noteText.trim(),
      status: this.notesForm.get('newNoteStatus')?.value,
      noteType: this.notesForm.get('newNoteType')?.value,
      visibility: 1,
      createDate: existing.dateTime.toISOString()
    };

    this.isSubmitting = true;
    this.managingNote = true;
    const sub = this.noteService.update(payload).pipe(take(1)).subscribe(
      res => {
        const updated = res?.data?.note || payload;
        const updatedNote: NoteItem = {
          ...existing,
          noteId: updated.noteId,
          notes: updated.description,
          noteType: updated.noteType,
          status: updated.status,
          title: updated.title || ''
        };

        this.noteUpdated.emit({ note: updatedNote, index: this.editingNoteIndex });
        this.cancelEdit();
        this.showFullscreenEditor = false;
        this.loadNotes(); // Reload notes to get latest from server
      },
      err => {
        // Do NOT reset isSubmitting on error — 504 means the backend may have saved.
        // Keep isSubmitting=true to block re-submit until the user reloads the page.
        this.alertService.error(err);
        this.managingNote = false;
      },
      () => { this.isSubmitting = false; /* note: managingNote is owned by loadNotes() from here */ }
    );
    
    this.subscriptions.push(sub);
  }

  editNote(note: any): void {
    const noteIndex = this.notes.findIndex((n: any) =>
      n === note || (n.noteId != null && n.noteId === note.noteId)
    );

    if (noteIndex === -1) {
      console.warn('Note not found in original notes array');
      return;
    }

    this.isEditingNote = true;
    this.editingNoteIndex = noteIndex;

    this.notesForm.patchValue({
      newNoteTitle: note.title ?? '',
      newNoteText: note.notes ?? '',
      newNoteType: note.noteType ?? '',
      newNoteStatus: note.status ?? ''
    });


    console.log(this.notesForm.value,'notesForm',note);
  }

  cancelEdit(): void {
    this.isEditingNote = false;
    this.editingNoteIndex = -1;
    this.resetForm();
  }

  deleteNote(note: NoteItem): void {
    const noteIndex = this.notes.findIndex(n => n === note);
    if (noteIndex < 0) return;

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this note? This action cannot be undone.',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (note.noteId) {
          this.managingNote = true;
          const sub = this.noteService.delete(note.noteId).pipe(take(1)).subscribe(
            () => {
              this.noteDeleted.emit({ note, index: noteIndex });
              this.adjustEditingIndex(noteIndex);
              this.managingNote = false;
              this.loadNotes(); // Reload notes to get latest from server
            },
            err => this.alertService.error(err)
          );
          this.subscriptions.push(sub);
        } else {
          this.noteDeleted.emit({ note, index: noteIndex });
          this.adjustEditingIndex(noteIndex);
          this.managingNote = false;
          this.loadNotes(); // Reload notes to get latest from server
        }
      },
      reject: () => { this.managingNote = false; /* User cancelled */ }
    });
  }

  private adjustEditingIndex(deletedIndex: number): void {
    if (this.isEditingNote) {

      if (this.editingNoteIndex === deletedIndex) {
        this.cancelEdit();
      } else if (this.editingNoteIndex > deletedIndex) {
        this.editingNoteIndex--;
      }
    }
  }

  toggleNoteExpansion(note: NoteItem): void {
    note.isExpanded = !note.isExpanded;
  }

  toggleFilterDropdown(): void {
    this.showFilterDropdown = !this.showFilterDropdown;
  }

  applyFilters(): void {
    this.activeFilters = { ...this.filterForm.value };
    this.showFilterDropdown = false;
    this.filtersChanged.emit(this.activeFilters);
  }

  clearFilters(): void {
    const resetValues: any = {};
    Object.keys(this.filterForm.value).forEach(key => {
      resetValues[key] = '';
    });
    
    this.filterForm.patchValue(resetValues);
    this.activeFilters = {};
    this.showFilterDropdown = false;
    this.filtersChanged.emit(this.activeFilters);
  }

  getActiveFilterCount(): number {
    return Object.values(this.activeFilters).filter(value => value).length;
  }

  toggleSortOrder(): void {
    switch (this.currentSortOrder) {
      case 'asc':
        this.currentSortOrder = 'desc';
        break;
      case 'desc':
        this.currentSortOrder = 'none';
        break;
      case 'none':
        this.currentSortOrder = 'asc';
        break;
    }
    
    this.sortNotes();
    this.sortChanged.emit(this.currentSortOrder);
  }

  private sortNotes(): void {
    if (this.currentSortOrder === 'none') return;
    
    this.notes.sort((a, b) => {
      const aTime = a.dateTime ? new Date(a.dateTime).getTime() : 0;
      const bTime = b.dateTime ? new Date(b.dateTime).getTime() : 0;
      return this.currentSortOrder === 'asc' ? (aTime - bTime) : (bTime - aTime);
    });
  }

  getSortIcon(): string {
    switch (this.currentSortOrder) {
      case 'asc':
        return 'fa fa-sort-amount-asc';
      case 'desc':
        return 'fa fa-sort-amount-desc';
      case 'none':
        return 'fa fa-sort';
      default:
        return 'fa fa-sort';
    }
  }

  getSortTooltip(): string {
    switch (this.currentSortOrder) {
      case 'asc':
        return 'Sort ascending';
      case 'desc':
        return 'Sort descending';
      case 'none':
        return 'No sorting';
      default:
        return 'Sort options';
    }
  }

  getFilterOptions(filterKey: string): any[] {
    switch (filterKey) {
      case 'noteType':
        return this.finalConfig.noteTypes || [];
      case 'status':
        return this.finalConfig.statusOptions || [];
      default:
        return [];
    }
  }

  getNoteTypeColor(noteType: any): string {
    const type = this.finalConfig.noteTypes?.find(t => t.value === noteType);
    return type ? type.color : '#D3D3D3';
  }

  getStatusColor(status: any): string {
    const statusOption = this.finalConfig.statusOptions?.find(s => s.value === status);
    return statusOption ? statusOption.color : '#D3D3D3';
  }

  getNoteTypeName(noteType: any): string {
    const type = this.finalConfig.noteTypes?.find(t => t.value === noteType);
    return type ? type.name : (noteType ?? '');
  }

  getStatusName(status: any): string {
    const statusOption = this.finalConfig.statusOptions?.find(s => s.value === status);
    return statusOption ? statusOption.name : (status ?? '');
  }

  /**
   * Get the best text color (white or black) for a given background color
   * Returns white for dark backgrounds, black for light backgrounds
   */
  getTextColorForBackground(backgroundColor: string): string {
    let r: number, g: number, b: number;
    
    // Handle hex colors
    if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.slice(1);
      
      // Handle 3-character hex codes
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else {
        // Handle 6-character hex codes
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    }
    // Handle rgb/rgba colors
    else if (backgroundColor.startsWith('rgb')) {
      const rgbMatch = backgroundColor.match(/\d+/g);
      if (rgbMatch && rgbMatch.length >= 3) {
        r = parseInt(rgbMatch[0], 10);
        g = parseInt(rgbMatch[1], 10);
        b = parseInt(rgbMatch[2], 10);
      } else {
        // Fallback to black for unknown formats
        return '#000000';
      }
    } else {
      // Fallback to black for unknown formats
      return '#000000';
    }

    // Calculate relative luminance (perceived brightness)
    // Using the formula from WCAG: https://www.w3.org/WAI/GL/wiki/Relative_luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // If luminance is less than 0.5, use white text; otherwise use black
    // Adjusted threshold to 0.55 for better visibility on bright colors
    return luminance < 0.55 ? '#FFFFFF' : '#000000';
  }

  getContainerClass(): string {
    const classes = ['notes-to-ops-container'];
    
    if (this.finalConfig.theme !== 'default') {
      classes.push(`theme-${this.finalConfig.theme}`);
    }
    
    if (this.finalConfig.customClass) {
      classes.push(this.finalConfig.customClass);
    }
    
    return classes.join(' ');
  }

  openFullscreen(): void {
    this.showFullscreen = !this.showFullscreen;
  }

  closeFullscreen(): void {
    this.showFullscreen = false;
    this.showFullscreenEditor = false;
    if (this.isEditingNote) {
      this.cancelEdit();
    }
  }

  openFullscreenEditorForAdd(): void {
    this.cancelEdit();
    this.popupEditorCycle++;
    this.showFullscreenEditor = false;
    setTimeout(() => {
      this.showFullscreenEditor = true;
    }, 0);
  }

  openFullscreenEditorForEdit(note: NoteItem): void {
    this.editNote(note);
    this.popupEditorCycle++;
    this.showFullscreenEditor = false;
    setTimeout(() => {
      this.showFullscreenEditor = true;
    }, 0);
  }

  closeFullscreenEditor(): void {
    this.showFullscreenEditor = false;
    if (this.isEditingNote) {
      this.cancelEdit();
    } else {
      this.resetForm();
    }
  }

  private resetForm(): void {
    this.notesForm.patchValue({
      newNoteTitle: '',
      newNoteText: '',
      newNoteType: this.finalConfig.noteTypes?.[0]?.value || 1,
      newNoteStatus: this.finalConfig.statusOptions?.[0]?.value || 0
    });
  }

  getEditorId(idSuffix?: string): string {
    const suffix = idSuffix || 'main';
    if (suffix === 'fs-edit') {
      return `${this.editorInstanceId}-${suffix}-${this.popupEditorCycle}`;
    }
    return `${this.editorInstanceId}-${suffix}`;
  }

  // Render helpers for HTML/text notes
  isNoteLong(note: NoteItem): boolean {
    const text = this.stripHtml(note?.notes || '');
    const limit = this.finalConfig?.maxNoteLength || 150;
    return text.length > limit;
  }

  getNotePreview(note: NoteItem): string {
    const text = this.stripHtml(note?.notes || '');
    const limit = this.finalConfig?.maxNoteLength || 150;
    return text.slice(0, limit);
  }

  getNotePreviewAsHtml(note: NoteItem): SafeHtml {
    const text = this.stripHtml(note?.notes || '');
    const limit = this.finalConfig?.maxNoteLength || 150;
    const preview = this.escapeHtmlForInnerHTML(text.slice(0, limit)).replace(/\n/g, '<br/>');
    return this.sanitizer.bypassSecurityTrustHtml(preview);
  }

  getNoteContentAsHtml(note: NoteItem): SafeHtml {
    const content = note?.notes || '';
    let html: string;
    if (this.hasHtml(content)) {
      html = content;
    } else {
      html = this.escapeHtmlForInnerHTML(content).replace(/\n/g, '<br/>');
    }
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  private hasHtml(content: string): boolean {
    if (!content) return false;
    return /<\/?[a-z][\s\S]*>/i.test(content);
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  private escapeHtmlForInnerHTML(text: string): string {
    const div = document.createElement('div');
    div.innerText = text;
    return div.innerHTML;
  }

  private updateUniqueUsers(): void {
    const users = new Set<string>();
    this.notes.forEach(note => {
      const employeeName = note.employee.trim();
      if (employeeName) {
        users.add(employeeName);
      }
    });
    
    this.uniqueUsers = Array.from(users).sort();
    
    // Update user options for multiselect
    this.userOptions = this.uniqueUsers.map(user => ({
      label: user,
      value: user
    }));
    
    // If no users are selected, select all by default
    if (this.selectedUsers.length === 0 && this.uniqueUsers.length > 0) {
      this.selectedUsers = [...this.uniqueUsers];
    } else {
      // Keep only valid selected users
      this.selectedUsers = this.selectedUsers.filter(user => this.uniqueUsers.includes(user));
    }
  }

  /**
   * Get the highlight color for a note if its type name matches any in highlightTypes
   */
  getHighlightColor(note: NoteItem): string | null {
    if (!this.finalConfig?.highlightTypes || this.finalConfig.highlightTypes.length === 0) {
      return null;
    }

    const noteTypeName = this.getNoteTypeName(note.noteType);
    const highlightType = this.finalConfig.highlightTypes.find(
      ht => ht.name.toLowerCase() === noteTypeName.toLowerCase()
    );

    return highlightType?.color ?? null;
  }

  /**
   * Get style object for note message with highlight color applied
   */
  getNoteMessageStyle(note: NoteItem): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const highlightColor = this.getHighlightColor(note);

    if (highlightColor) {
      // Apply highlight color as background with opacity for better text readability
      styles['background-color'] = this.addOpacityToColor(highlightColor, 0.15);
      styles['border-left-color'] = highlightColor;
      styles['border-left-width'] = '4px';
      styles['border-left-style'] = 'solid';
    }

    return styles;
  }

  /**
   * Helper method to add opacity to a color (converts hex to rgba)
   * Enhanced to make colors more highlighted and ensure text visibility
   */
  private addOpacityToColor(color: string, opacity: number): string {
    let r: number, g: number, b: number;
    
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      
      // Handle 3-character hex codes
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else {
        // Handle 6-character hex codes
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    }
    // Handle rgb/rgba colors
    else if (color.startsWith('rgb')) {
      // Extract RGB values
      const rgbMatch = color.match(/\d+/g);
      if (rgbMatch && rgbMatch.length >= 3) {
        r = parseInt(rgbMatch[0], 10);
        g = parseInt(rgbMatch[1], 10);
        b = parseInt(rgbMatch[2], 10);
      } else {
        // Fallback if parsing fails
        return color;
      }
    } else {
      // Fallback: assume it's a valid color and return as-is
      return color;
    }

    // Calculate brightness (luminance) to determine if color is dark or light
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Increase opacity for better visibility (minimum 0.3, maximum 0.5)
    // Darker colors can use higher opacity, lighter colors need lower opacity for text visibility
    let adjustedOpacity = opacity;
    if (brightness < 128) {
      // Dark colors: use higher opacity (0.35-0.45) for better highlight
      adjustedOpacity = Math.min(0.45, Math.max(0.35, opacity * 2.5));
      // Lighten dark colors slightly for better visibility on white background
      // Blend with white (255, 255, 255) to make it brighter
      const lightenFactor = 0.15; // 15% blend with white
      r = Math.min(255, Math.round(r + (255 - r) * lightenFactor));
      g = Math.min(255, Math.round(g + (255 - g) * lightenFactor));
      b = Math.min(255, Math.round(b + (255 - b) * lightenFactor));
    } else {
      // Light colors: use moderate opacity (0.3-0.4) to maintain text readability
      adjustedOpacity = Math.min(0.4, Math.max(0.3, opacity * 2));
      // Slightly saturate light colors to make them more vibrant
      const saturateFactor = 1.1; // 10% more saturation
      const avg = (r + g + b) / 3;
      r = Math.min(255, Math.max(0, Math.round(avg + (r - avg) * saturateFactor)));
      g = Math.min(255, Math.max(0, Math.round(avg + (g - avg) * saturateFactor)));
      b = Math.min(255, Math.max(0, Math.round(avg + (b - avg) * saturateFactor)));
    }

    return `rgba(${r}, ${g}, ${b}, ${adjustedOpacity})`;
  }
}